import React, { memo } from 'react';
import { Button, Dropdown, message, Switch, Tooltip } from 'antd';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  useDeleteAssetReviewLinkMutation,
  useUpdateAssetReviewMutation
} from '@hooks/queries/assetReview';
import { toggleShareAssetModal } from '@redux/actions/modalAction';
import { ActionToast } from '@components/Toasts';
import { AssetItemDto, AssetPathItemDto, AssetReviewItemDto } from '@api/Api';
import ActionFolder from '@pages/Campaigns/CampaignReview/components/Actions/ActionFolder';
import {
  ActionIcon,
  ArrowDownIcon,
  ArrowLeftWithoutLineIcon,
  DeleteIcon,
  DropdownLinkIcon,
  DropdownRenameIcon,
  DropdownSettingsLinearIcon,
  ExternalLinkIcon,
  FolderIcon,
  FolderPrivateIcon,
  LockIcon,
  UnlockIcon
} from '@icons';
import './Head.less';

type HeadProps = {
  review: AssetReviewItemDto;
  campaignId: string;
  currentFolder: AssetPathItemDto | null;
  currentFolderPath: AssetPathItemDto[];
  assetsToDownload: AssetItemDto[];
  canManageReviews: boolean;
  onGoBack: () => void;
  onNavigateToFolder: (targetFolderId: string | null) => void;
};

export default memo(function Head({
  review,
  campaignId,
  currentFolder,
  currentFolderPath,
  assetsToDownload,
  onGoBack,
  onNavigateToFolder,
  canManageReviews
}: HeadProps) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { mutateAsync: updateAssetReview, isPending: isUpdatingAssetReview } =
    useUpdateAssetReviewMutation({});

  const { mutateAsync: deleteAssetReviewLink } =
    useDeleteAssetReviewLinkMutation({});

  const onOpenLinkSettings = (defaultActiveTab = 'sharing') => {
    dispatch(
      toggleShareAssetModal({
        visible: true,
        reviewId: review.id,
        defaultActiveTab
      })
    );
  };

  const onActiveChange = async (v: boolean) => {
    await updateAssetReview({
      reviewId: review.id,
      active: v
    });
  };

  const onRemoveLink = () => {
    let action = true;
    toast(
      <ActionToast
        title="Link removed"
        onUndo={() => {
          action = false;
        }}
        description={undefined}
        closeToast={undefined}
      />,
      {
        onClose: () => {
          if (action) {
            deleteAssetReviewLink({ reviewId: review.id });
            navigate(`/campaigns/single/${campaignId}?tab=sharedLinks`);
          }
        }
      }
    );
  };

  if (currentFolder) {
    return (
      <div className="campaign-review-head">
        <div className="campaign-review-head-breadcrumbs">
          <Button onClick={onGoBack} type="ghost">
            <ArrowLeftWithoutLineIcon />
          </Button>
          <div
            className="campaign-review-head-breadcrumbs-item"
            onClick={() => onNavigateToFolder(null)}
          >
            <Tooltip
              title={review.name}
              overlayClassName="toggle_favorite_overlay toggle_favorite_overlay--small"
            >
              {review.name}
            </Tooltip>
          </div>
          {currentFolderPath.map((pathItem, index) => {
            const isLast = index === currentFolderPath.length - 1;
            return (
              <React.Fragment key={pathItem.id}>
                <ArrowDownIcon className="campaign-review-head-breadcrumbs-separator" />
                <div
                  className={`campaign-review-head-breadcrumbs-item${
                    isLast ? ' b-current' : ''
                  }`}
                  onClick={
                    isLast ? undefined : () => onNavigateToFolder(pathItem.id)
                  }
                >
                  <Tooltip
                    title={pathItem.name}
                    overlayClassName="toggle_favorite_overlay toggle_favorite_overlay--small"
                  >
                    {pathItem.name}
                  </Tooltip>
                </div>
              </React.Fragment>
            );
          })}
        </div>
        <div className="campaign-review-head-info">
          <div className="campaign-review-head-title">
            {currentFolder.isPrivate ? <FolderPrivateIcon /> : <FolderIcon />}
            {currentFolder.name}
            <ActionFolder
              canManageReviews={canManageReviews}
              assets={assetsToDownload}
              folderName={currentFolder.name}
              reviewId={review.id}
              folderId={currentFolder.id}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="campaign-review-head">
      <div className="campaign-review-head-info">
        <div className="campaign-review-head-title">
          {review.name}
          <Dropdown
            menu={{
              items: [
                ...(canManageReviews
                  ? [
                      {
                        key: 'renameLink',
                        label: 'Rename',
                        icon: <DropdownRenameIcon />,
                        onClick: () => onOpenLinkSettings()
                      },
                      {
                        type: 'divider' as const
                      }
                    ]
                  : []),
                {
                  key: 'copyLink',
                  label: 'Copy link',
                  icon: <DropdownLinkIcon />,
                  onClick: () => {
                    message.info('Copied to clipboard');
                    navigator.clipboard.writeText(review.url);
                  }
                },
                {
                  key: 'previewInNewTab',
                  label: 'Preview in new tab',
                  icon: <ExternalLinkIcon />,
                  onClick: () => {
                    window.open(review.url, '_blank');
                  }
                },
                ...(canManageReviews
                  ? [
                      {
                        type: 'divider' as const
                      },
                      {
                        key: 'removeLink',
                        label: 'Delete',
                        icon: <DeleteIcon />,
                        onClick: onRemoveLink
                      }
                    ]
                  : [])
              ]
            }}
            overlayClassName="asset_links_overlay"
            placement="bottomLeft"
            trigger={['click']}
          >
            <Button
              className="asset_links_action"
              onClick={(e) => e.stopPropagation()}
              type="text"
              size="small"
              style={{ width: 24, height: 24 }}
            >
              <ActionIcon />
            </Button>
          </Dropdown>
        </div>
        <div className="campaign-review-head-text">
          <Tooltip
            placement="bottomLeft"
            overlayClassName="toggle_favorite_overlay"
            title={review.password ? 'Password enabled' : 'Password disabled'}
          >
            {review.password ? <LockIcon /> : <UnlockIcon />}
          </Tooltip>
          {(review.allowComments || review.allowDownloads) && (
            <span>
              Allow: {review.allowComments && 'comments'}
              {review.allowDownloads && review.allowComments && ', '}
              {review.allowDownloads && 'downloads'}
            </span>
          )}
        </div>
      </div>
      {canManageReviews && (
        <div className="campaign-review-head-actions">
          <div>
            <Switch
              loading={isUpdatingAssetReview}
              onChange={(v: boolean) => onActiveChange(v)}
              checked={review.active}
              style={{ marginRight: 12 }}
            />
            {review.active ? 'On' : 'Off'}
          </div>
          <Button
            type="ghost"
            icon={<DropdownSettingsLinearIcon style={{ fontSize: 24 }} />}
            onClick={() => onOpenLinkSettings('settings')}
          >
            Link settings
          </Button>
        </div>
      )}
    </div>
  );
});
